/**
 * Admin Modules — V2.N.7
 *
 * Montre quelles pages/modules les visiteurs activent ou désactivent
 * (agrégé depuis device_pages). Permet de voir l'usage réel des
 * fonctionnalités.
 *
 * Lit /api/admin/devices?action=modules_usage. Style Stripe light.
 */

import { useEffect, useState } from 'react';
import { fetchDevices } from '../lib/fetchAdminAction';
import { adminPageStyle, adminCardStyle, adminTableStyle, adminThStyle, adminTdStyle } from '../lib/adminStyles';
import '../layout/AdminLayout.css';

interface ModuleRow { module: string; enabled: number; disabled: number; }
interface UsageData { total_devices: number; modules: ModuleRow[]; }

export function Modules() {
  const [data, setData] = useState<UsageData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await fetchDevices<UsageData>('modules_usage');
      if (!cancelled) { setData(d); setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const total = data?.total_devices ?? 0;

  return (
    <main style={adminPageStyle}>
      <header style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Modules & pages</h1>
        <div style={{ fontSize: '13px', color: 'var(--muted)', marginTop: '4px' }}>
          Usage réel des fonctionnalités — activées / désactivées par {total} appareils.
        </div>
      </header>

      <section style={adminCardStyle}>
        {loading ? (
          <div style={{ fontSize: '13px', color: 'var(--muted)' }}>Chargement…</div>
        ) : !data || data.modules.length === 0 ? (
          <div style={{ fontSize: '12px', color: 'var(--muted)', fontStyle: 'italic' }}>
            Aucune donnée de modules. (Apparaît après le sync V2.N.4 quand des visiteurs ont configuré leurs pages.)
          </div>
        ) : (
          <table style={adminTableStyle}>
            <thead>
              <tr>
                <th style={adminThStyle}>Module</th>
                <th style={adminThStyle}>Activé</th>
                <th style={adminThStyle}>Désactivé</th>
                <th style={{ ...adminThStyle, width: '40%' }}>Ratio activé</th>
              </tr>
            </thead>
            <tbody>
              {data.modules.map((m) => {
                const tot = m.enabled + m.disabled;
                const pct = tot ? (m.enabled / tot) * 100 : 0;
                return (
                  <tr key={m.module}>
                    <td style={{ ...adminTdStyle, fontWeight: 600 }}>{m.module}</td>
                    <td style={{ ...adminTdStyle, color: '#0e9f6e' }}>{m.enabled}</td>
                    <td style={{ ...adminTdStyle, color: 'var(--muted)' }}>{m.disabled}</td>
                    <td style={adminTdStyle}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div style={{ flex: 1, height: '12px', background: 'rgba(105,115,134,0.12)', borderRadius: '4px', overflow: 'hidden' }}>
                          <div style={{ width: `${pct}%`, height: '100%', background: '#0e9f6e' }} />
                        </div>
                        <span style={{ fontSize: '11px', color: 'var(--muted)', width: '40px', textAlign: 'right' }}>{pct.toFixed(0)} %</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>
    </main>
  );
}

